import type { AccessModule } from '@goyone/shared';
import type { IconName } from '../components/Icon';
import type { SessionUser } from '../auth/AuthProvider';

/**
 * خريطة التنقّل — مصدر واحد للتطبيقات وقوائمها.
 *
 * الشريط العلوي والجانبي والصلاحيات كلها تُشتق من هنا، فإضافة شاشة تعني
 * سطرًا واحدًا لا تعديلًا في ثلاثة أماكن قد يتخلّف أحدها عن الباقي.
 *
 * التقسيم يتبع مسؤولية كل فريق لا ترتيب البناء:
 *
 *   إدارة العملاء  من يتابع العميل: العملاء والحسابات والتواصل والمواعيد.
 *   المبيعات       من يبيع: الصفقات والقمع وعروض الأسعار والعقود والكتالوج.
 *   الحسابات       من يحاسب: الفواتير والتحصيل والمصروفات والقيود.
 *
 * الصفقات والقمع كانا في إدارة العملاء وهو موضع خاطئ: الصفقة أداة بيع لا
 * أداة متابعة، ومندوب المبيعات يعمل عليها في سياق عروض الأسعار لا في سياق
 * المكالمات.
 */

export interface NavItem {
  path: string;
  label: string;
  icon?: IconName;
  /** أدنى دور يرى هذا العنصر. */
  minRole?: SessionUser['role'];
  /** شاشة لم تُبنَ بعد — تُعرض معطّلة بدل إخفائها. */
  soon?: boolean;
  /**
   * مفتاح الوحدة في الباقة لعنصرٍ يسكن تطبيقًا ليس له.
   *
   * المراقبةُ والتقاريرُ صارتا في الرئيسية، والرئيسيةُ للجميع — فبدون
   * المفتاح يرى من لا يملك وحدة التقارير رابطًا يفتح شاشةً ترفضه.
   */
  feature?: string;
  /** وحدةُ ملفّ الصلاحيات — تغلب وحدةَ المجموعة والتطبيق. */
  module?: AccessModule;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  module?: AccessModule;
  /**
   * تظهر مهما ضاق ملفُّ الصلاحيات: ما يخصّ الموظّفَ عن نفسه — ملفُّه
   * الشخصيّ وإجازاتُه وحضورُه — لا تحجبه وحدةٌ مغلقة.
   */
  always?: boolean;
}

export interface NavApp {
  key: string;
  label: string;
  /** مفتاح الوحدة في الباقة. الغياب يعني إتاحتها للجميع. */
  feature?: string;
  base: string;
  /**
   * مسارات إضافية يملكها التطبيق خارج قاعدته.
   *
   * الأصول تنتمي إلى الحسابات مفهومًا — إهلاكها قيد في الدفتر — لكن رابطها
   * /assets لا /finance/assets. وبدون هذا الحقل لا يجد النظام لها تطبيقًا
   * فيعود إلى الأول، فتنقلب القائمة الجانبية إلى قائمة الرئيسية ويجد
   * المستخدم نفسه خارج القسم الذي فتحه.
   */
  owns?: readonly string[];
  groups: NavGroup[];
  soon?: boolean;
  /** وحدةُ ملفّ الصلاحيات التي يخضع لها التطبيقُ كلُّه. */
  module?: AccessModule;
}

export const APPS: NavApp[] = [
  {
    key: 'home',
    label: 'الرئيسية',
    base: '/',
    /*
     * المراقبةُ والتقاريرُ في الرئيسية لا قسمين في الشريط.
     *
     * كلاهما نظرةٌ على الشركة كلّها لا عملُ فريقٍ بعينه، ومن يفتح النظام
     * صباحًا يسأل «ما الذي يجري؟» في مكانٍ واحد. وقسمان بعنصرين وثلاثة
     * يزاحمان في الشريط أقسامًا فيها عشرات الشاشات.
     *
     * والروابط لم تتغيّر: من حفظ `/reports/sales` يصل إليها كما كان.
     *
     * والشاتُ خرج من القائمة إلى فقاعةٍ عائمة تُفتح فوق أيّ شاشة — ومسارُه
     * باقٍ لمن يريده بملء الصفحة.
     */
    owns: ['/monitor', '/reports', '/chat'],
    groups: [
      {
        title: 'نظرة عامة',
        items: [
          { path: '/', label: 'لوحة اليوم', icon: 'home' },
          { path: '/my-work', label: 'أعمالي', icon: 'clipboard' },
        ],
      },
      {
        title: 'مركز المراقبة',
        module: 'monitor',
        /* لمن يدير فقط — المراقبة أداة إشراف لا عمل يومي. */
        items: [
          {
            path: '/monitor',
            label: 'شاشة المراقبة',
            icon: 'video',
            minRole: 'MANAGER',
            feature: 'crm',
          },
          /* إعداداتُ المراقبة في الإعدادات — كلُّ ضبطٍ في مكانٍ واحد. */
        ],
      },
      {
        /*
         * لوحاتُ الأداء صارت في وحداتها (لوحة المبيعات، لوحة الحسابات…)
         * ولوحةُ النظام في الرئيسية نفسها — فبقي هنا الباني وحدَه.
         */
        title: 'التقارير',
        module: 'reports',
        items: [
          {
            path: '/reports/builder',
            label: 'باني التقارير',
            icon: 'layers',
            feature: 'reports',
          },
        ],
      },
    ],
  },

  {
    key: 'crm',
    label: 'إدارة العملاء',
    feature: 'crm',
    module: 'crm',
    base: '/crm',
    groups: [
      {
        /*
         * «العملاء» لا «الحسابات».
         *
         * الجدول اسمه `accounts` والمسار `/crm/accounts` ولن يتغيّرا — لكن
         * من يفتح الشاشة يبحث عن عملائه، و«حساب» في نظام فيه دليل حسابات
         * وقيود يُقرأ حسابًا محاسبيًا. التسمية في الواجهة تتبع من يقرأها
         * لا من كتب المخطط.
         *
         * وعنوان المجموعة تغيّر معه: مجموعة «العملاء» فيها عنصر «العملاء»
         * تكرارٌ يُربك.
         */
        title: 'قاعدة العملاء',
        items: [
          { path: '/crm/dashboard', label: 'لوحة المعلومات', icon: 'chartBar', feature: 'reports' },
          { path: '/crm/leads', label: 'العملاء المحتملون', icon: 'funnel' },
          { path: '/crm/accounts', label: 'العملاء', icon: 'building' },
          { path: '/crm/contacts', label: 'جهات الاتصال', icon: 'users' },
        ],
      },
      {
        title: 'التواصل',
        items: [
          { path: '/crm/activities', label: 'الأنشطة', icon: 'clipboard' },
          { path: '/crm/inbox', label: 'البريد الوارد', icon: 'mail' },
          { path: '/crm/channels', label: 'قنوات ميتا', icon: 'chat' },
          { path: '/crm/meetings', label: 'الاجتماعات', icon: 'video' },
        ],
      },
      {
        title: 'التنظيم',
        /* لوح المهام القديم حُذف — مهام العمل صارت في «إدارة المشاريع». */
        items: [
          { path: '/crm/calendar', label: 'التقويم', icon: 'calendar' },
          { path: '/crm/war-room', label: 'غرفة العمليات', icon: 'zap' },
        ],
      },
    ],
  },

  {
    key: 'callcenter',
    label: 'مركز الاتصال',
    /*
     * تحت مفتاح إدارة العملاء في الباقة: المكالمة أداة متابعة عميل،
     * وفصلها في مفتاح اشتراك جديد يحرم من يملك CRM من مكالماته.
     */
    feature: 'crm',
    module: 'callcenter',
    base: '/callcenter',
    groups: [
      {
        title: 'العمل',
        items: [
          { path: '/callcenter', label: 'الكونسول', icon: 'phone' },
          {
            path: '/callcenter/history',
            label: 'سجلّ المكالمات',
            icon: 'clock',
          },
        ],
      },
      {
        title: 'الإدارة',
        items: [
          {
            path: '/callcenter/dashboard',
            label: 'لوحة الأداء',
            icon: 'chartBar',
            minRole: 'MANAGER',
          },
          {
            path: '/callcenter/insights',
            label: 'التقارير المنشورة',
            icon: 'chart',
            minRole: 'MANAGER',
            feature: 'reports',
          },
          {
            path: '/callcenter/quality',
            label: 'جودة المكالمات',
            icon: 'check',
          },
        ],
      },
    ],
  },

  {
    key: 'sales',
    label: 'المبيعات',
    feature: 'sales',
    module: 'sales',
    base: '/sales',
    owns: ['/my/commissions'],
    groups: [
      {
        title: 'الصفقات',
        items: [
          {
            path: '/sales/dashboard',
            label: 'لوحة المعلومات',
            icon: 'chartBar',
            feature: 'reports',
          },
          { path: '/sales/deals', label: 'الصفقات', icon: 'chart' },
          { path: '/sales/pipeline', label: 'قمع المبيعات', icon: 'funnel' },
          { path: '/sales/lost', label: 'تحليل الخسائر', icon: 'alert' },
        ],
      },
      {
        title: 'المستندات',
        items: [
          /*
           * صفحاتُ الحجز في المبيعات لا في إدارة العملاء.
           *
           * صارت الصفحةُ تفتح صفقةً وعرضَ سعرٍ بنفسها، وتُسعَّر من كتالوج
           * الخدمات والباقات — فهي بابُ بيعٍ لا سجلَّ عميل.
           */
          {
            path: '/sales/booking',
            label: 'صفحات الحجز',
            icon: 'send',
            minRole: 'MANAGER',
          },
          {
            path: '/sales/quotations',
            label: 'عروض الأسعار',
            icon: 'clipboard',
          },
          { path: '/sales/contracts', label: 'العقود', icon: 'clipboard' },
        ],
      },
      {
        title: 'الفريق',
        items: [
          /*
           * الفريقُ هنا، والأهدافُ والخصوماتُ في الإعدادات.
           *
           * «الأهداف والخصومات» شاشةُ ضبطٍ لا شاشةُ عمل: تُفتح مرّةً في
           * الشهر وتُنسى. وما يُفتح كلَّ يوم هو الفريقُ نفسُه — من يبيع
           * وبأيّ هدفٍ ونسبة. فصُدّر الفريقُ إلى القائمة، وبقيت شاشةُ
           * الضبط حيث تُطلب: في الإعدادات.
           */
          {
            path: '/sales/team',
            label: 'فريق المبيعات',
            icon: 'users',
            minRole: 'MANAGER',
          },
          {
            path: '/sales/commissions',
            label: 'العمولات',
            icon: 'wallet',
            minRole: 'MANAGER',
          },
          { path: '/my/commissions', label: 'عمولاتي', icon: 'user' },
        ],
      },
      {
        title: 'الإعداد',
        items: [
          { path: '/sales/services', label: 'كتالوج الخدمات', icon: 'inbox' },
          { path: '/sales/packages', label: 'الباقات', icon: 'layers' },
        ],
      },
    ],
  },

  {
    key: 'agency',
    label: 'إدارة المشاريع',
    /*
     * تحت مفتاح إدارة العملاء في الباقة: تنفيذ أعمال العميل امتدادٌ
     * لإدارته، ومفتاحُ اشتراكٍ جديد كان سيحرم من يملك CRM من التنفيذ.
     */
    feature: 'crm',
    module: 'agency',
    base: '/agency',
    /*
     * القائمة رحلةُ العميل بترتيب محطاتها: يوقّع فيُجهَّز، ثم يُدار عمله
     * اليومي، ثم يستقرّ على باقةٍ شهرية — لا «مراحل» مرقّمة تُقرأ إداريًّا.
     */
    groups: [
      /*
       * الترتيبُ من الأعلى إلى الأدنى: هدفٌ تخدمه محافظُ عملٍ، وفي كلّ
       * محفظةٍ مشاريع، وفي كلّ مشروعٍ مهامّ. فمن فتح القسم قرأ إلى أين
       * تمضي الشركة قبل أن يقرأ ما يُنجَز اليوم.
       */
      {
        title: 'نظرة عامة',
        items: [
          { path: '/agency', label: 'الرئيسية', icon: 'home' },
          {
            path: '/agency/dashboard',
            label: 'لوحة المعلومات',
            icon: 'chartBar',
            feature: 'reports',
          },
          { path: '/agency/goals', label: 'الأهداف', icon: 'target' },
        ],
      },
      {
        title: 'خطوط العمل',
        items: [
          { path: '/agency/portfolios', label: 'المحافظ', icon: 'folder' },
          { path: '/agency/projects', label: 'المشاريع', icon: 'briefcase' },
        ],
      },
      {
        title: 'رحلة العميل',
        items: [
          {
            path: '/agency/onboarding',
            label: 'العقود والتجهيز',
            icon: 'rocket',
          },
          { path: '/agency/retainers', label: 'باقات العملاء', icon: 'layers' },
        ],
      },
      {
        title: 'الفريق',
        /* قوالبُ المشاريع في الإعدادات: تُبنى مرّةً وتُطبَّق كلَّ يوم. */
        items: [{ path: '/agency/team', label: 'فريق العمل', icon: 'users' }],
      },
      {
        title: 'عملي',
        items: [{ path: '/agency/my', label: 'مهامي', icon: 'check' }],
      },
    ],
  },

  {
    key: 'finance',
    label: 'الحسابات',
    feature: 'finance',
    module: 'finance',
    base: '/finance',
    owns: ['/assets'],
    groups: [
      {
        title: 'الذمم',
        items: [
          {
            path: '/finance/dashboard',
            label: 'لوحة المعلومات',
            icon: 'chartBar',
            minRole: 'MANAGER',
            feature: 'reports',
          },
          {
            path: '/finance/invoices',
            label: 'فواتير العملاء',
            icon: 'wallet',
          },
          {
            path: '/finance/recurring',
            label: 'الاشتراكات الدورية',
            icon: 'refresh',
            minRole: 'MANAGER',
          },
          { path: '/finance/payments', label: 'التحصيلات', icon: 'wallet' },
          {
            path: '/finance/receivables',
            label: 'أرصدة العملاء',
            icon: 'users',
          },
          {
            path: '/finance/statements',
            label: 'كشف حساب عميل',
            icon: 'receipt',
          },
        ],
      },
      {
        title: 'المشتريات والمصروفات',
        items: [
          { path: '/finance/suppliers', label: 'الموردون', icon: 'building' },
          { path: '/finance/bills', label: 'فواتير الموردين', icon: 'inbox' },
          {
            path: '/finance/recurring-bills',
            label: 'الاشتراكات الدورية للموردين',
            icon: 'refresh',
            minRole: 'MANAGER',
          },
          { path: '/finance/expenses', label: 'المصروفات', icon: 'wallet' },
          {
            path: '/finance/payables',
            label: 'أرصدة الموردين',
            icon: 'building',
          },
          {
            path: '/finance/supplier-statements',
            label: 'كشف حساب مورد',
            icon: 'receipt',
          },
        ],
      },
      {
        title: 'الخزينة',
        items: [
          {
            path: '/finance/banks',
            label: 'البنوك والخزائن',
            icon: 'building',
          },
        ],
      },
      {
        title: 'الأصول الثابتة',
        items: [
          {
            path: '/assets',
            label: 'سجل الأصول',
            icon: 'layers',
            minRole: 'MANAGER',
          },
          {
            path: '/assets/depreciation',
            label: 'الإهلاك',
            icon: 'chart',
            minRole: 'ADMIN',
          },
          {
            path: '/assets/counts',
            label: 'الجرد',
            icon: 'clipboard',
            minRole: 'MANAGER',
          },
        ],
      },
      {
        title: 'الدفتر العام',
        items: [
          {
            path: '/finance/accounts',
            label: 'دليل الحسابات',
            icon: 'layers',
            minRole: 'MANAGER',
          },
          {
            path: '/finance/journal',
            label: 'القيود اليومية',
            icon: 'clipboard',
            minRole: 'MANAGER',
          },
        ],
      },
      {
        title: 'التقارير',
        items: [
          { path: '/finance/aging', label: 'تقادم الذمم', icon: 'chart' },
          {
            path: '/finance/credit',
            label: 'متابعة الائتمان',
            icon: 'alert',
            minRole: 'MANAGER',
          },
          {
            path: '/finance/reports',
            label: 'القوائم المالية',
            icon: 'chartBar',
            minRole: 'MANAGER',
          },
        ],
      },
    ],
  },

  {
    key: 'hr',
    label: 'الموارد البشرية',
    feature: 'hr',
    module: 'hr',
    base: '/hr',
    /*
     * القائمةُ هي الهيكل.
     *
     * عشرُ إداراتٍ تحت مدير الموارد البشرية، مجموعةٌ لكلّ إدارةٍ وتحتها
     * تفاصيلُها. وما لم يُبنَ يبقى في موضعه معلَّمًا «قريبًا» لا يُحذف:
     * من يفتح القسم يريد أن يرى شكلَ إدارته كاملًا ويعرف أين هو منه —
     * وقائمةٌ تُخفي سبعَ إداراتٍ تجعله يظنّ أنّ هذا كلُّ ما هنالك.
     *
     * والترتيبُ ترتيبُ الإدارات كما تُرسم في الهيكل، لا ترتيبُ ما بُني.
     */
    groups: [
      {
        /*
         * رأسُ القسم: الصورةُ ثمّ الأرقام.
         *
         * اللوحةُ تسكن هنا لا في إدارتها العاشرة: هي ما يُفتح أوّلًا كلَّ
         * صباحٍ ويُقرأ في دقيقة، ودفنُها آخرَ قائمةٍ من اثنتي عشرة مجموعةً
         * يجعلها لا تُفتح. والإدارةُ تبقى في مكانها من الهيكل.
         */
        title: 'الموارد البشرية',
        items: [
          { path: '/hr', label: 'الهيكل', icon: 'layers' },
          {
            path: '/hr/analytics',
            label: 'لوحة المؤشّرات',
            icon: 'chart',
            minRole: 'MANAGER',
          },
          {
            path: '/hr/dashboard',
            label: 'التقارير المنشورة',
            icon: 'chartBar',
            minRole: 'MANAGER',
            feature: 'reports',
          },
        ],
      },
      /*
       * إعدادُ المؤسسة (الشركة والورديات والهيكل والوحدات) في الإعدادات:
       * يُضبط مرّةً ثمّ تختار منه بقيّةُ الشاشات.
       */
      {
        /*
         * التوظيفُ أوّلًا لأنّه يسبق غيرَه في الزمن: الموظّفُ كان مرشّحًا
         * قبل أن يكون ملفًّا. والترتيبُ داخله رحلةُ الشخص لا ترتيبُ
         * الجداول — نظرةٌ، فوظائفُ تُفتح، فمسارٌ يمشي فيه من تقدّم.
         */
        title: 'التوظيف والاستقطاب',
        /*
         * الترتيبُ رحلةُ الشاغر لا ترتيبُ الجداول.
         *
         * وظيفةٌ تُكتب، فاستمارةٌ تُبنى لها، فحملةٌ تنشرها، فطلباتٌ تصل،
         * فمواعيدُ تُحدَّد، فمسارٌ يمشي فيه من تقدّم حتى يُعيَّن. ومن يفتح
         * القائمةَ أوّلَ مرّةٍ يمشي من أعلى إلى أسفل فيجد نفسَه قد بنى
         * المنظومةَ كاملةً بلا أن يُقال له من أين يبدأ.
         */
        items: [
          { path: '/hr/recruit', label: 'نظرة عامة', icon: 'target' },
          { path: '/hr/recruit/jobs', label: 'الوظائف', icon: 'briefcase' },
          {
            path: '/hr/recruit/campaigns',
            label: 'حملات التعيين',
            icon: 'send',
            minRole: 'MANAGER',
          },
          {
            path: '/hr/recruit/applications',
            label: 'طلبات التقديم',
            icon: 'inbox',
          },
          {
            path: '/hr/recruit/agenda',
            label: 'مواعيد المقابلات',
            icon: 'clock',
          },
          { path: '/hr/recruit/pipeline', label: 'المسار', icon: 'funnel' },
        ],
      },
      {
        title: 'شؤون العاملين',
        /*
         * المرشَّحُ للتعيين بابٌ قبل الموظّف.
         *
         * هو أوّلُ ما يصل من التوظيف: قَبِل العرضَ وفُتح ملفُّه ولم يبدأ.
         * وخلطُه بمن هم على رأس العمل يجعل من ينتظر عقدًا يضيع بين مئة
         * ملفّ — وهو الذي يحتاج عملًا اليوم.
         */
        items: [
          {
            path: '/hr/employees/pending',
            label: 'ملفات المرشّحين للتعيين',
            icon: 'inbox',
            minRole: 'MANAGER',
          },
          { path: '/hr/employees', label: 'ملفات الموظفين العاملين', icon: 'users' },
          { path: '/hr/attendance', label: 'الحضور', icon: 'calendar' },
          { path: '/hr/leaves', label: 'الإجازات', icon: 'clipboard' },
          {
            path: '/hr/requests',
            label: 'الطلبات',
            icon: 'inbox',
            minRole: 'MANAGER',
          },
          {
            path: '/hr/contracts',
            label: 'عقود العمل',
            icon: 'folder',
            minRole: 'ADMIN',
          },
          {
            path: '/hr/documents',
            label: 'المستندات والأوراق',
            icon: 'paperclip',
            minRole: 'MANAGER',
          },
        ],
      },
      {
        title: 'الرواتب والمزايا',
        items: [
          {
            path: '/hr/payroll',
            label: 'دورات المرتب',
            icon: 'wallet',
            minRole: 'ADMIN',
          },
          {
            path: '/hr/benefits',
            label: 'المزايا والتأمين الطبي',
            icon: 'diamond',
            minRole: 'ADMIN',
          },
        ],
      },
      {
        title: 'التدريب والتطوير',
        /*
         * بابٌ واحدٌ لا بابان.
         *
         * الاحتياجُ والدورةُ والانعقادُ والأثرُ سلسلةٌ واحدة: من يفتح
         * «الخطّة» وحدَها لا يجد ما يخطّط له، ومن يفتح «الدورات» وحدَها
         * يكتب كتالوجًا لا ينعقد.
         */
        items: [
          {
            path: '/hr/learning',
            label: 'الاحتياجات والدورات',
            icon: 'book',
            minRole: 'MANAGER',
          },
        ],
      },
      {
        title: 'إدارة الأداء',
        /*
         * بابٌ واحدٌ بثلاثة ألسنة لا ثلاثةُ أبواب.
         *
         * الدورةُ والأهدافُ والخطّةُ ثلاثةُ وجوهٍ لعملٍ واحدٍ يُفتح في جلسةٍ
         * واحدة: يُكتب الهدفُ أوّلَ المدّة، ويُقاس في الدورة، وتُفتح الخطّةُ
         * لمن احتاجها. وتفريقُها على القائمة يجعل من يفتح لسانًا يظنّ أنّه
         * انتهى.
         */
        items: [
          {
            path: '/hr/performance',
            label: 'الأداء: دورات وأهداف وخطط',
            icon: 'chartBar',
            minRole: 'ADMIN',
          },
        ],
      },
      {
        title: 'التطوير التنظيمي',
        items: [
          { path: '/hr/org', label: 'الهيكل التنظيمي', icon: 'layers' },
          {
            path: '/hr/org/positions',
            label: 'الأوصاف الوظيفية',
            icon: 'clipboard',
            minRole: 'MANAGER',
          },
          {
            path: '/hr/org/succession',
            label: 'خطط الإحلال',
            icon: 'arrowRightLeft',
            minRole: 'MANAGER',
          },
          {
            path: '/hr/org/planning',
            label: 'تخطيط القوى العاملة',
            icon: 'chart',
            minRole: 'MANAGER',
          },
        ],
      },
      {
        title: 'علاقات الموظفين',
        /*
         * بابٌ واحدٌ لمدير النظام.
         *
         * الحالةُ تحمل شكوى موظّفٍ على مديره وما سُمع في التحقيق، وفتحُها
         * لكلّ من يدير فريقًا يعني أن يقرأ المشكوُّ منه شكوى من اشتكاه.
         */
        items: [
          {
            path: '/hr/relations',
            label: 'الحالات والإجراءات والرضا',
            icon: 'chat',
            minRole: 'ADMIN',
          },
        ],
      },
      {
        title: 'إدارة المواهب',
        /*
         * والشراكةُ لسانٌ فيها لا بابٌ بجانبها.
         *
         * شريكُ الوحدة هو من يحمل مواهبَها ويُسأل عن حالاتها، وفصلُه في
         * بابٍ ثانٍ يجعل جدولَ التغطية يُفتح مرّةً ثمّ يُنسى.
         */
        items: [
          {
            path: '/hr/talent',
            label: 'المصفوفة والشركاء',
            icon: 'star',
            minRole: 'ADMIN',
          },
        ],
      },
      {
        /*
         * بوّابةُ الموظّف — لكلّ من له حسابٌ لا للإدارة وحدَها.
         *
         * شاشاتُ الموارد البشرية كلُّها فوقها حارسُ رتبةٍ لأنّها تحمل رواتبَ
         * الناس وأرقامَهم القومية. وهذه الشاشاتُ لا تقبل معرّفَ موظّفٍ أصلًا:
         * تقرأ صاحبَ الجلسة وحدَه — فمن أراد أرقامَه هو يجدها هنا.
         */
        title: 'بياناتي',
        always: true,
        items: [
          { path: '/my/profile', label: 'ملفّي', icon: 'user' },
          { path: '/my/leaves', label: 'إجازاتي', icon: 'calendar' },
          { path: '/my/requests', label: 'طلباتي', icon: 'inbox' },
          { path: '/my/attendance', label: 'حضوري', icon: 'clock' },
          { path: '/my/payslips', label: 'رواتبي', icon: 'wallet' },
          { path: '/my/custody', label: 'عهدتي', icon: 'briefcase' },
        ],
      },
    ],
    /* مسارات البوّابة خارج قاعدة `/hr` — تُعلَن ليعرف الهيكلُ صاحبَها. */
    owns: [
      '/my/profile',
      '/my/leaves',
      '/my/requests',
      '/my/attendance',
      '/my/payslips',
      '/my/custody',
    ],
  },

  {
    /*
     * حسابي — قسمٌ شخصيٌّ مستقلٌّ عن إعدادات الشركة.
     *
     * الملفُّ والاشتراكُ والتفضيلاتُ تخصّ الشخصَ لا المؤسسة، فتُفصل عن
     * «الإعدادات» التي يضبطها المديرُ للشركة كلِّها. يُفتح من الصورة في
     * الشريط، ولا يظهر تطبيقًا في شريط التطبيقات.
     */
    key: 'account',
    label: 'حسابي',
    base: '/account',
    groups: [
      {
        title: 'حسابي',
        always: true,
        items: [
          { path: '/account', label: 'الملف الشخصي', icon: 'user' },
          {
            path: '/account/subscription',
            label: 'الاشتراك والدفع',
            icon: 'wallet',
            minRole: 'ADMIN',
          },
        ],
      },
    ],
  },

  {
    key: 'settings',
    label: 'الإعدادات',
    base: '/settings',
    module: 'settings',
    owns: ['/notifications'],
    /*
     * الإعداداتُ كلُّها هنا — ولا ضبطَ في مكانٍ آخر من النظام.
     *
     * الترتيبُ: صاحبُ الحساب أوّلًا (ملفُّه وباقتُه)، ثمّ من يعمل معه
     * وصلاحياتُهم، ثمّ الشركةُ، ثمّ وحدةٌ وحدةٌ بترتيب الشريط العلويّ،
     * ثمّ المراقبةُ والتكاملات. وما يخصّ وحدةً لا يملكها المستأجرُ يُخفى.
     */
    groups: [
      {
        /* تفضيلٌ شخصيٌّ يُفتح من الترس — لا في «حسابي». */
        title: 'تفضيلاتي',
        always: true,
        items: [{ path: '/settings/notifications', label: 'الإشعارات', icon: 'alert' }],
      },
      {
        title: 'المستخدمون والصلاحيات',
        items: [
          {
            path: '/settings/users',
            label: 'المستخدمون',
            icon: 'users',
            minRole: 'ADMIN',
          },
          {
            path: '/settings/roles',
            label: 'ملفّات الصلاحيات',
            icon: 'layers',
            minRole: 'ADMIN',
          },
        ],
      },
      {
        title: 'الشركة',
        items: [
          {
            path: '/settings/company',
            label: 'بيانات الشركة',
            icon: 'building',
            minRole: 'ADMIN',
          },
          {
            path: '/settings/branding',
            label: 'الهوية والمستندات',
            icon: 'layers',
            minRole: 'ADMIN',
          },
          {
            path: '/settings/finance-defaults',
            label: 'الإعدادات المالية',
            icon: 'receipt',
            minRole: 'ADMIN',
          },
          {
            path: '/settings/security',
            label: 'الحماية',
            icon: 'alert',
            minRole: 'MANAGER',
          },
          {
            path: '/settings/audit',
            label: 'سجل التدقيق',
            icon: 'book',
            minRole: 'ADMIN',
          },
        ],
      },
      {
        title: 'البيانات',
        items: [
          {
            path: '/settings/data',
            label: 'تصدير واستيراد',
            icon: 'download',
            minRole: 'MANAGER',
          },
        ],
      },
      {
        title: 'إدارة العملاء',
        items: [
          {
            path: '/settings/war-room-rules',
            label: 'قواعد الرصد',
            icon: 'zap',
            minRole: 'MANAGER',
            feature: 'crm',
          },
          {
            path: '/settings/custom-fields',
            label: 'الحقول المخصَّصة',
            icon: 'layers',
            minRole: 'ADMIN',
            feature: 'crm',
          },
          {
            path: '/settings/lookups',
            label: 'قوائم القيم',
            icon: 'inbox',
            minRole: 'ADMIN',
            feature: 'crm',
          },
          {
            path: '/settings/ratings',
            label: 'تقييم العملاء',
            icon: 'star',
            minRole: 'ADMIN',
            feature: 'crm',
          },
          {
            path: '/settings/automation',
            label: 'الأتمتة وإشعارات العميل',
            icon: 'zap',
            minRole: 'ADMIN',
            feature: 'crm',
          },
          {
            path: '/settings/messages',
            label: 'الرسائل المحفوظة',
            icon: 'note',
            minRole: 'MANAGER',
            feature: 'crm',
          },
        ],
      },
      {
        title: 'المبيعات',
        items: [
          {
            path: '/settings/commissions',
            label: 'العمولات والخصومات',
            icon: 'wallet',
            minRole: 'ADMIN',
            feature: 'sales',
          },
          {
            path: '/settings/sales-targets',
            label: 'أهداف المندوبين',
            icon: 'target',
            minRole: 'MANAGER',
            feature: 'sales',
          },
          {
            path: '/settings/meetings',
            label: 'الاجتماعات والحجز',
            icon: 'video',
            minRole: 'ADMIN',
            feature: 'sales',
          },
        ],
      },
      {
        title: 'مركز الاتصال',
        items: [
          {
            path: '/settings/callcenter',
            label: 'الاتصال والنتائج والتسجيل',
            icon: 'phone',
            minRole: 'ADMIN',
            feature: 'crm',
          },
        ],
      },
      {
        title: 'إدارة المشاريع',
        items: [
          {
            path: '/settings/project-templates',
            label: 'قوالب المشاريع',
            icon: 'gantt',
            minRole: 'MANAGER',
            feature: 'crm',
          },
        ],
      },
      {
        title: 'الحسابات',
        items: [
          {
            path: '/settings/asset-categories',
            label: 'فئات الأصول والإهلاك',
            icon: 'layers',
            minRole: 'ADMIN',
            feature: 'finance',
          },
          {
            path: '/settings/credit-rules',
            label: 'قواعد الائتمان',
            icon: 'alert',
            minRole: 'ADMIN',
            feature: 'finance',
          },
        ],
      },
      {
        title: 'الموارد البشرية',
        items: [
          {
            path: '/settings/hr-company',
            label: 'الورديات ومراكز التكلفة',
            icon: 'building',
            minRole: 'MANAGER',
            feature: 'hr',
          },
          {
            path: '/settings/org-structure',
            label: 'الهيكل التنظيمي',
            icon: 'gantt',
            minRole: 'MANAGER',
            feature: 'hr',
          },
          {
            path: '/settings/org-units',
            label: 'الوحدات التنظيمية',
            icon: 'layers',
            minRole: 'MANAGER',
            feature: 'hr',
          },
          {
            path: '/settings/worksites',
            label: 'مواقع العمل',
            icon: 'mapPin',
            minRole: 'MANAGER',
            feature: 'hr',
          },
          {
            path: '/settings/grades',
            label: 'هيكل الرواتب والدرجات',
            icon: 'wallet',
            minRole: 'ADMIN',
            feature: 'hr',
          },
          {
            path: '/settings/request-types',
            label: 'قواعد الطلبات',
            icon: 'clipboard',
            minRole: 'ADMIN',
            feature: 'hr',
          },
          {
            path: '/settings/payroll-rules',
            label: 'التأمينات والضريبة',
            icon: 'receipt',
            minRole: 'ADMIN',
            feature: 'hr',
          },
          {
            path: '/settings/application-forms',
            label: 'استمارات التقديم',
            icon: 'clipboard',
            minRole: 'MANAGER',
            feature: 'hr',
          },
        ],
      },
      {
        /*
         * كلُّ ما يخصّ المراقبةَ في مكانٍ واحد: تطبيقُ سطح المكتب الذي
         * تعمل من خلاله، وإذنُ الموظّف بمشاركة شاشته، وإعداداتُ المدير
         * لوضع الالتقاط ومدّته. التطبيقُ والإذنُ للجميع، والإعداداتُ للمدير.
         */
        title: 'المراقبة',
        always: true,
        items: [
          {
            path: '/settings/desktop-app',
            label: 'تطبيق سطح المكتب',
            icon: 'download',
          },
          {
            path: '/settings/screen-monitoring',
            label: 'إذن مراقبة الشاشة',
            icon: 'video',
          },
          {
            path: '/settings/monitoring',
            label: 'إعدادات المراقبة',
            icon: 'settings',
            minRole: 'ADMIN',
            feature: 'crm',
          },
        ],
      },
      {
        title: 'التكاملات',
        items: [
          {
            path: '/settings/mail',
            label: 'البريد الإلكتروني',
            icon: 'mail',
            minRole: 'ADMIN',
          },
          {
            path: '/settings/meta',
            label: 'قنوات ميتا',
            icon: 'chat',
            minRole: 'ADMIN',
          },
          {
            path: '/settings/drive',
            label: 'Google Drive',
            icon: 'download',
            minRole: 'ADMIN',
          },
        ],
      },
    ],
  },
];

/** التطبيق الذي ينتمي إليه المسار الحالي. */
/**
 * اسمُ الشاشة الحالية كما يقرؤه الإنسان — لنبضة نشاط المراقبة.
 *
 * يُبحث عن أخصّ عنصرٍ يطابق المسار في كل التطبيقات، فيصير في اللوحة «عروض
 * الأسعار» لا «/sales/quotations». وما لا عنصرَ له (شاشة تفصيلٍ مثلًا)
 * يأخذ اسم تطبيقه، فلا يبقى بلا اسم.
 */
export function labelForPath(pathname: string): string {
  let best: { label: string; len: number } | null = null;
  for (const app of APPS) {
    for (const group of app.groups) {
      for (const item of group.items) {
        if (
          (pathname === item.path || pathname.startsWith(`${item.path}/`)) &&
          (!best || item.path.length > best.len)
        ) {
          best = { label: item.label, len: item.path.length };
        }
      }
    }
  }
  return best?.label ?? appForPath(pathname).label;
}

export function appForPath(pathname: string): NavApp {
  /*
   * الأطول أولًا.
   *
   * المسار قد يطابق أكثر من بادئة، والأخصّ هو الصحيح — وإلا ابتلع تطبيقٌ
   * قاعدتُه أقصر مسارًا يخصّ غيره.
   */
  const candidates = APPS.flatMap((app) =>
    [app.base, ...(app.owns ?? [])]
      .filter((prefix) => prefix !== '/')
      .filter((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
      .map((prefix) => ({ app, prefix })),
  ).sort((a, b) => b.prefix.length - a.prefix.length);

  return candidates[0]?.app ?? APPS[0]!;
}
